import { useMemo } from "react";
import { Badge } from "react-bootstrap";
import { getPlanType } from "modules/pricingv2/utils";
import { IoIosAlert } from "react-icons/io";
import moment from "moment";
import { formatCurrency } from "utils/formatNumber";
import { DAYS_ON_HAND_LABELS, getInventoryStatus } from "modules/pricingv2/utils";
import { trellisPalette } from "components/custom/analytics/palettes";
import { ForecastBadge } from "modules/pricingv2/utils";

const PlanHeader = ({ pricingPlan, marketPlace, forecastBucket }) => {
  const daysOnHandStatus = useMemo(() => {
    return getInventoryStatus(pricingPlan);
  }, [pricingPlan]);

  const lastDaysOrders = [];
  const orders = pricingPlan.order_history !== null ? Object.values(pricingPlan.order_history) : [];
  if (orders.length > 14) {
    lastDaysOrders.push(...orders.slice(orders.length - 15, orders.length - 1));
  }
  let pacing = 0;
  if (lastDaysOrders.length > 0) {
    pacing = Math.round((lastDaysOrders.reduce((acc, order) => acc + order.qty, 0) / lastDaysOrders.length) * 30, 0);
  }

  const currentPrice = pricingPlan?.best_price;
  const minPrice = pricingPlan?.min_price;
  const maxPrice = pricingPlan?.max_price;
  const minPriceDiff = parseFloat(Math.abs((currentPrice - minPrice) / minPrice).toFixed(2));
  const maxPriceDiff = parseFloat(Math.abs((maxPrice - currentPrice) / maxPrice).toFixed(2));

  return (
    <>
      <div style={{ display: "flex", width: "100%" }} className="text-start mb-3">
        {getPlanType(pricingPlan) === "Dynamic Price" && (
          <div style={{ flex: 1 }} className="pe-4">
            <h6 style={{ fontWeight: 600 }} className="fs-standard text-uppercase pt-1">
              Strategy
            </h6>

            <Badge
              className={`badge bg-trellis-${pricingPlan.enroll_date ? "purple" : "lightgrey"} text-end`}
              style={{
                borderRadius: "5px",
              }}
            >
              {pricingPlan.enroll_date &&
                pricingPlan.configured_slider_value >= 0 &&
                pricingPlan.configured_slider_value <= 0 && <span>Balanced</span>}
              {pricingPlan.enroll_date && pricingPlan.configured_slider_value < 0 && <span>Volume</span>}
              {pricingPlan.enroll_date && pricingPlan.configured_slider_value > 0 && <span>Margin</span>}
              {!pricingPlan.enroll_date && <span>n/a</span>}
            </Badge>
          </div>
        )}
        <div style={{ flex: 1 }} className="pe-4">
          <h6 className="text-uppercase pt-1">Enroll Date</h6>
          <span className="fs-5">{moment(pricingPlan.enroll_date).format("MM/DD/YYYY")}</span>
        </div>
        {pricingPlan?.best_price && (
          <div style={{ flex: 1 }} className="pe-4 text-center">
            <h6 className="text-uppercase pt-1">Price</h6>
            <span className="fs-5">{formatCurrency(pricingPlan.best_price, marketPlace.marketPlace)}</span>

            {minPriceDiff <= 0.02 && (
              <span className="ps-1 fs-3" style={{ color: trellisPalette[2] }}>
                <IoIosAlert />
              </span>
            )}
            {maxPriceDiff <= 0.02 && (
              <span className="ps-1 fs-3" style={{ color: trellisPalette[2] }}>
                <IoIosAlert />
              </span>
            )}
          </div>
        )}

        {pricingPlan?.best_price_date && (
          <div style={{ flex: 1 }} className="pe-4 text-center">
            <h6 className="text-uppercase pt-1">Optimized On</h6>
            <span className="fs-5">{moment(pricingPlan.best_price_date).format("MM/DD/YYYY")}</span>
          </div>
        )}

        {pricingPlan?.override_supply && (
          <div style={{ flex: 1 }} className="pe-4 text-center">
            <h6 style={{ fontWeight: 600 }} className="fs-standard text-uppercase pt-1">
              Days on Hand
            </h6>

            <Badge
              className={`badge bg-primary bg-trellis-${DAYS_ON_HAND_LABELS[daysOnHandStatus]} text-end`}
              style={{
                borderRadius: "5px",
              }}
            >
              {daysOnHandStatus}
            </Badge>
          </div>
        )}

        {pricingPlan?.override_units && (
          <div style={{ flex: 1 }} className="pe-4 text-center">
            <h6 className="text-uppercase pt-1">Target Units</h6>

            <Badge
              className={`badge bg-primary bg-trellis-${DAYS_ON_HAND_LABELS[daysOnHandStatus]} text-end`}
              style={{
                borderRadius: "5px",
              }}
            ></Badge>
            <span className="fs-5 fw-bold">{Math.round(pricingPlan.target_units * 30, 2)}</span>
          </div>
        )}

        {pacing > 0 && (
          <div style={{ flex: 1 }} className="pe-4 text-center">
            <h6 className="text-uppercase pt-1">Pacing</h6>

            <span className="fs-5 fw-bold">{pacing}</span>
          </div>
        )}
      </div>
      <div style={{ display: "flex", width: "100%" }}>
        <div style={{ flex: 1 }} className="pe-4 text-start">
          <h6 className="text-uppercase pt-1">Tags</h6>
          {pricingPlan.tags.map((tag, index) => (
            <Badge
              className={`badge bg-trellis-mauve text-end me-3`}
              style={{
                borderRadius: "10px",
              }}
              key={index}
            >
              {tag}
            </Badge>
          ))}
        </div>
        {forecastBucket && (
          <div style={{ flex: 1 }} className="pe-4 text-start">
            <h6 className="text-uppercase pt-1">Forecast Bucket</h6>
            <ForecastBadge bucket={forecastBucket} />
          </div>
        )}
      </div>
    </>
  );
};

export default PlanHeader;
